import { createTheme } from "@mui/material/styles";
import { theme, tokens } from "../theme";

/**
 * Tema de las tablas. Se construye SOBRE el tema de la app (createTheme(theme, ...))
 * para que tipografía y paleta no se pierdan donde se envuelve con ThemeProvider.
 * Todas las tablas de mui-datatables del sistema heredan de acá.
 */
export const MuiTheme = createTheme(theme, {
  components: {
    // Contenedor: la tabla es una tarjeta, no una hoja suelta.
    MUIDataTable: {
      styleOverrides: {
        paper: {
          backgroundColor: tokens.surface,
          border: `1px solid ${tokens.border}`,
          borderRadius: `${tokens.radiusCard}px`,
          boxShadow: "none",
          overflow: "hidden",
        },
        responsiveBase: {
          // Las tablas anchas scrollean dentro de su tarjeta, nunca la página.
          overflowX: "auto",
        },
        tableRoot: { borderCollapse: "collapse" },
        /**
         * mui-datatables repite el `title` dentro de un <caption> de la tabla.
         * Como nuestro título ES la barra de herramientas, ese duplicado se
         * colaba como una franja clara sobre el encabezado y además metía los
         * mismos controles dos veces en el árbol de accesibilidad.
         */
        caption: {
          display: "none",
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: { boxShadow: "none", backgroundImage: "none" },
      },
    },

    // Barra superior de la tabla: título + búsqueda + acciones.
    MUIDataTableToolbar: {
      styleOverrides: {
        root: {
          minHeight: 56,
          paddingLeft: 16,
          paddingRight: 10,
          display: "flex",
          alignItems: "flex-start",
          // Había dos `gap` en este mismo objeto: el primero (8) nunca se aplicaba.
          gap: 4,
          borderBottom: `1px solid ${tokens.borderSoft}`,
          backgroundColor: tokens.surface,
          // Con la búsqueda siempre abierta, el botón de lupa quedaba duplicado.
          '& [data-testid="Search-iconButton"]': { display: "none" },
        },
        // La barra propia viaja como `title`: tiene que ocupar todo el ancho.
        left: { flexGrow: 1, minWidth: 0, padding: 0 },
        titleRoot: { width: "100%" },
        titleText: {
          fontSize: "14px !important",
          fontWeight: "600 !important",
          color: tokens.ink,
          width: "100%",
        },
        actions: {
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 2,
        },
        icon: {
          color: tokens.ink2,
          borderRadius: tokens.radiusControl,
          "&:hover": { color: tokens.ink, backgroundColor: tokens.borderSoft },
        },
        iconActive: { color: tokens.brandDark },
      },
    },

    MUIDataTableSearch: {
      styleOverrides: {
        main: { gap: 8 },
        searchIcon: { color: tokens.ink3, marginTop: 0 },
        searchText: {
          flex: "1 0 auto",
          "& .MuiInput-root": {
            height: 36,
            paddingLeft: 12,
            paddingRight: 12,
            borderRadius: tokens.radiusControl,
            border: `1px solid ${tokens.border}`,
            backgroundColor: tokens.surface2,
            fontSize: "13px",
          },
          "& .MuiInput-root:before, & .MuiInput-root:after": { display: "none" },
          "& .MuiInput-root.Mui-focused": {
            borderColor: tokens.brand,
            boxShadow: `0 0 0 3px ${tokens.brandTint}`,
            backgroundColor: tokens.surface,
          },
        },
        clearIcon: { color: tokens.ink3 },
      },
    },

    // Encabezado de columna.
    MUIDataTableHeadRow: {
      styleOverrides: { root: { backgroundColor: tokens.surface2 } },
    },
    MUIDataTableHeadCell: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.surface2,
          borderBottom: `1px solid ${tokens.border}`,
          padding: "9px 12px",
          whiteSpace: "nowrap",
        },
        fixedHeader: { backgroundColor: tokens.surface2 },
        data: {
          fontSize: "11px",
          fontWeight: 600,
          color: tokens.ink3,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
        },
        sortAction: { alignItems: "center" },
        sortActive: { color: tokens.ink },
        toolButton: {
          marginLeft: 0,
          marginRight: 0,
          borderRadius: 6,
          "&:hover": { backgroundColor: tokens.borderSoft },
        },
      },
    },

    // Filas.
    // Una sola línea continua por fila, dibujada por la fila misma.
    MuiTable: {
      styleOverrides: { root: { borderCollapse: "collapse" } },
    },
    MUIDataTableBodyRow: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid #f5f3f1",
          "&:hover": { backgroundColor: `${tokens.surface2} !important` },
          "&:last-child": { borderBottom: "none" },
        },
      },
    },
    MUIDataTableBodyCell: {
      styleOverrides: {
        root: {
          // Denso a propósito: entra más información en una sola vista.
          padding: "8px 12px",
          fontSize: "13px",
          lineHeight: 1.35,
          color: tokens.ink,
          borderBottom: "none",
          verticalAlign: "middle",
          maxWidth: 280,
        },
        stackedHeader: { color: tokens.ink3, fontWeight: 600 },
        cellHide: { display: "none" },
        stackedCommon: { borderBottom: "none" },
        stackedParent: { borderBottom: "none" },
        responsiveStackedSmall: { borderBottom: "none" },
        responsiveStacked: { borderBottom: "none" },
      },
    },

    MUIDataTableSelectCell: {
      styleOverrides: { headerCell: { backgroundColor: tokens.surface2 } },
    },

    // Paginación.
    MUIDataTablePagination: {
      styleOverrides: {
        root: { borderTop: `1px solid ${tokens.borderSoft}` },
        tableCellContainer: { padding: "4px 8px", borderBottom: "none" },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        toolbar: { minHeight: 52, paddingLeft: 12 },
        selectLabel: { fontSize: "12.5px", color: tokens.ink3, marginBottom: 0 },
        displayedRows: { fontSize: "12.5px", color: tokens.ink3, marginBottom: 0 },
        select: { fontSize: "12.5px" },
        actions: {
          "& .MuiIconButton-root": {
            borderRadius: tokens.radiusControl,
            "&:hover": { backgroundColor: tokens.borderSoft },
          },
        },
      },
    },

    // Panel de filtros.
    MUIDataTableFilter: {
      styleOverrides: {
        root: { padding: "18px 20px 20px", backgroundColor: tokens.surface },
        title: { fontSize: "14px", fontWeight: 600, color: tokens.ink },
        resetLink: { fontSize: "12.5px", color: tokens.brandDark, fontWeight: 500 },
        checkboxFormControlLabel: { fontSize: "13px" },
      },
    },
    MUIDataTableFilterList: {
      styleOverrides: {
        root: { margin: "10px 14px 0" },
        chip: {
          borderRadius: 999,
          backgroundColor: tokens.borderSoft,
          color: tokens.ink,
          fontSize: "12px",
          fontWeight: 500,
        },
      },
    },

    MUIDataTableViewCol: {
      styleOverrides: {
        root: { padding: "18px 22px" },
        title: { fontSize: "14px", fontWeight: 600, color: tokens.ink },
        label: { fontSize: "13px" },
      },
    },

    // Mensaje de "sin resultados".
    MUIDataTableBody: {
      styleOverrides: { emptyTitle: { color: tokens.ink3, fontSize: "13.5px" } },
    },

    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: "#f5f3f1" },
        footer: { borderBottom: "none" },
      },
    },

    MuiList: {
      styleOverrides: { root: { display: "flex", flexDirection: "column" } },
    },
  },
});

export const textLabels = {
  body: {
    noMatch: "Lo sentimos, no se encontraron registros",
    toolTip: "Ordenar",
    columnHeaderTooltip: (column) => `Orden para ${column.label}`,
  },
  pagination: {
    next: "Página siguiente",
    previous: "Página anterior",
    rowsPerPage: "Filas por página:",
    displayRows: "de",
  },
  toolbar: {
    search: "Buscar",
    downloadCsv: "Descargar archivo Excel",
    print: "Imprimir",
    viewColumns: "Ver columnas",
    filterTable: "Filtrar tabla",
  },
  filter: {
    all: "Todo",
    title: "Filtros",
    reset: "Borrar",
  },
  viewColumns: {
    title: "Mostrar columnas",
    titleAria: "Mostrar/ocultar columnas de la tabla",
  },
  selectedRows: {
    text: "fila(s) seleccionadas",
    delete: "Eliminar",
    deleteAria: "Eliminar las filas seleccionadas",
  },
};
